/**
 * SPRINT V4.5 — Cliffhanger entre partidos.
 * Cuando termina un partido, propone el siguiente con cuenta regresiva,
 * puntos necesarios para superar al rival y conteo de predicciones ya
 * realizadas. CTA "Entrar ahora".
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Timer } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";

export interface CliffhangerProps {
  nextMatchLabel?: string;
  nextKickoffAt?: number; // epoch ms
  rivalNationCode?: string | null;
  pointsToOvertake?: number;
  predictionsAlready?: number;
  onEnter?: () => void;
}

export function CliffhangerCard({
  nextMatchLabel = "Próximo partido",
  nextKickoffAt = Date.now() + 1000 * 60 * 45,
  rivalNationCode = "ARG",
  pointsToOvertake = 214,
  predictionsAlready = 12480,
  onEnter,
}: CliffhangerProps) {
  const { nation } = useSelectedNation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    void trackEvent("cliffhanger_shown", { rival: rivalNationCode, pointsToOvertake });
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [rivalNationCode, pointsToOvertake]);

  const remaining = useMemo(() => Math.max(0, nextKickoffAt - now), [nextKickoffAt, now]);
  const m = Math.floor(remaining / 60000);
  const s = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-background/40 to-background/40 p-4">
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary">
        <Timer className="h-3 w-3" /> No pares ahora
      </div>
      <div className="mt-1 text-sm font-bold">{nextMatchLabel}</div>
      <div className="mt-2 flex items-baseline gap-2 tabular-nums">
        <span className="text-3xl font-black text-primary">{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">para el inicio</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg bg-background/60 px-2 py-1.5">
          <div className="text-[9px] uppercase text-muted-foreground">Para superar a</div>
          <div className="font-bold">{getNationFlag(rivalNationCode)} {getNationName(rivalNationCode)}</div>
          <div className="text-primary font-black tabular-nums">+{pointsToOvertake} pts</div>
        </div>
        <div className="rounded-lg bg-background/60 px-2 py-1.5">
          <div className="text-[9px] uppercase text-muted-foreground">{getNationFlag(nation)} Tu nación</div>
          <div className="font-bold tabular-nums">{predictionsAlready.toLocaleString()}</div>
          <div className="text-[10px] text-muted-foreground">predicciones ya hechas</div>
        </div>
      </div>
      <button
        onClick={() => {
          void trackEvent("cliffhanger_cta_clicked", { rival: rivalNationCode });
          onEnter?.();
        }}
        className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground"
      >
        Entrar ahora <ChevronRight className="h-3 w-3" />
      </button>
    </div>
  );
}